import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getTenantFeatureFlags, updateTenantFeatureFlags } from '@/api/apiFeatureFlags';
import type { FeatureFlagsResponse, UpdateFeatureFlagsPayload } from '@/api/apiTypes';

export function useTenantFeatureFlags(tenantId?: number) {
  return useQuery({
    queryKey: ['tenant-feature-flags', tenantId],
    queryFn: () => getTenantFeatureFlags(tenantId as number),
    enabled: tenantId !== undefined,
    retry: false, // Don't retry when backend isn't ready
    staleTime: 30000, // Cache for 30 seconds
  });
}

export function useUpdateTenantFeatureFlags(tenantId?: number) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: UpdateFeatureFlagsPayload) =>
      updateTenantFeatureFlags(tenantId as number, payload),

    // Optimistic update: immediately update UI before server responds
    onMutate: async (payload: UpdateFeatureFlagsPayload) => {
      if (tenantId === undefined) {
        return;
      }

      const queryKey = ['tenant-feature-flags', tenantId];

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<FeatureFlagsResponse>(queryKey);

      // Optimistically update to the new value
      if (previousData) {
        const updatedFeatures = previousData.features.map((feature) => {
          const update = payload.features.find((f) => f.name === feature.name);
          return update ? { ...feature, enabled: update.enabled } : feature;
        });

        queryClient.setQueryData<FeatureFlagsResponse>(queryKey, {
          ...previousData,
          features: updatedFeatures,
        });
      }

      // Return context object with the snapshotted value
      return { previousData };
    },

    // If mutation fails, use the context returned from onMutate to roll back
    onError: (_err, _payload, context) => {
      if (context?.previousData && tenantId !== undefined) {
        queryClient.setQueryData(['tenant-feature-flags', tenantId], context.previousData);
      }
    },

    // Always refetch after error or success to ensure we're in sync with server
    onSettled: () => {
      if (tenantId !== undefined) {
        queryClient.invalidateQueries({ queryKey: ['tenant-feature-flags', tenantId] });
      }
    },
  });
}
